import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { TaxSettingsStatus } from "@modules/finances/gateway/taxesDto"
import { TaxDialogContext } from "@modules/finances/taxes/components/taxDialogManager"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useContext } from "react"

export const useTaxes = () => {
  const { boxFinanceApi } = useServicesContext()
  const queryClient = useQueryClient()
  const { result: box } = useCurrentBox()
  const {
    activateTaxDialogVisible,
    taxSettingsDialogVisible,
    setActivateTaxDialogVisible,
    setTaxSettingsDialogVisible,
  } = useContext(TaxDialogContext)

  const { mutateAsync: activateTaxes, isPending: isPendingActivateTaxes } = useMutation({
    mutationFn: async () => {
      return await boxFinanceApi.activateTaxes()
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["box", box?.id],
      })
    },
  })

  const { mutateAsync: getTaxRegistrationToken, isPending: isPendingTaxRegistrationToken } = useMutation({
    mutationFn: async () => {
      return await boxFinanceApi.getTaxesRegistrationToken()
    },
    gcTime: 0,
  })

  const openTaxSettings = () => {
    if (box?.taxStatus === TaxSettingsStatus.ACTIVE) {
      setTaxSettingsDialogVisible(true)
      return
    }

    setActivateTaxDialogVisible(true)
  }

  const handleActivateTaxSettings = async () => {
    try {
      await activateTaxes()
      setActivateTaxDialogVisible(false)
      setTaxSettingsDialogVisible(true)
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        "message" in error &&
        error.status === 400 &&
        typeof error.message === "string" &&
        error.message.includes("Tax setting can't be activate for country")
      ) {
        toast.error(word("finances.taxes.dialog.activate.error.countryNotAllowed"), {
          classNames: errorToastClassName(),
        })
      } else {
        toast.error(word("global.error.support"), {
          classNames: errorToastClassName(),
        })
      }
    } finally {
      setActivateTaxDialogVisible(false)
    }
  }

  return {
    taxStatus: box?.taxStatus,
    activateTaxes,
    getTaxRegistrationToken,
    isPendingActivateTaxes,
    isPendingTaxRegistrationToken,
    activateTaxDialogVisible,
    taxSettingsDialogVisible,
    setActivateTaxDialogVisible,
    setTaxSettingsDialogVisible,
    openTaxSettings,
    handleActivateTaxSettings,
  }
}
